import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { useCallback } from 'react'
import { Formik } from 'formik'
import {
  AddServiceLocationFormModel,
  validateExternalServiceLocationForm,
  validateServiceLocationForm,
} from './formModel'
import { AddServiceLocationFormProps } from './typings'
import { useData, useOnSubmit } from './hooks'
import { FormFields } from './FormFields'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import { useCheckPermissions } from '@user/store/authorization/permissions'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'

export const AddServiceLocationForm: React.FC<AddServiceLocationFormProps> = (
  props,
) => {
  const {
    externalLocation = false,
    newExternalLocation = false,
    isBillingLocation,
    matched,
  } = props
  const hasInitialValues = props.initialValues !== undefined

  const {
    loading,
    locationTypes,
    divisions,
    branches,
    hydratedValues,
    runPestPacValidation,
    isMappedUser,
  } = useData(props)

  const [hasUpdateLocationsPermission] = useCheckPermissions('accounts_update')
  const canUpdateLocations =
    !matched || (isMappedUser && hasUpdateLocationsPermission)

  //The "Update Bill-to" checkbox should be shown only for an unmatched lead/opp
  //AND if the user is either unmapped, OR mapped and has the "Locations Update" permission
  const showUpdateCheckbox = !isBillingLocation && canUpdateLocations
  const { validatePostalCode } = useLocalisedValidations()

  const onSubmit = useOnSubmit(props, showUpdateCheckbox)

  const validate = useCallback(
    (model: AddServiceLocationFormModel) => {
      return externalLocation
        ? validateExternalServiceLocationForm(
            model,
            runPestPacValidation,
            validatePostalCode,
          )
        : validateServiceLocationForm(
            model,
            runPestPacValidation || newExternalLocation,
            validatePostalCode,
          )
    },
    [
      externalLocation,
      runPestPacValidation,
      newExternalLocation,
      validatePostalCode,
    ],
  )

  const theme = useTheme()

  return loading ? (
    <Box width="100%" height="auto" display="flex">
      <CircularProgress
        color="primary"
        style={{ margin: 'auto', marginTop: theme.spacing(10) }}
        size={60}
      />
    </Box>
  ) : (
    <Formik
      onSubmit={onSubmit}
      validate={validate}
      initialValues={hydratedValues}
      validateOnMount
    >
      <FormFields
        {...props}
        showAddLabel={!hasInitialValues}
        locationTypes={locationTypes}
        divisions={divisions}
        branches={branches}
        showUpdateCheckbox={showUpdateCheckbox}
        runPestPacValidation={runPestPacValidation}
      />
    </Formik>
  )
}
AddServiceLocationForm.displayName = 'AddServiceLocationForm'
